import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UserDto } from '@interfaces/UserDto';
import { TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import { firstValueFrom, lastValueFrom, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, shareReplay, take, tap } from 'rxjs/operators';
import { BaseService } from './base.service';

@Injectable({
  providedIn: 'root'
})
export class UserService extends BaseService {
  public user: UserDto | null = null;
  private user$: ReplaySubject<UserDto> = null;

  public constructor(
    private http: HttpClient,
    private router: Router,
    private logger: NGXLogger,
    private translateService: TranslateService
  ) {
    super();
  }

  public getUserAsync(): Promise<UserDto | null> {
    return firstValueFrom(this.getUser().pipe(take(1)));
  }

  public getUser(): Observable<UserDto | null> {
    if (this.user$ !== null) {
      return this.user$;
    }

    this.user$ = new ReplaySubject<UserDto>();

    this.http
      .get<UserDto>(this.baseUrl + '/web/user/')
      .pipe(
        tap(user => {
          this.user = user;
          this.user$.next(user);
        }),
        shareReplay(),
        catchError((err, caught) => {
          this.logger.error('UserService.getUser() : ' + err);
          this.logger.error('UserService.getUser() : ' + caught);

          if (err.status === 401) {
            this.logger.info(`Status: ${err.status}: Redirecting to 401`);
            this.router.navigate(['/401']);
          }

          if (err.status === 403) {
            this.logger.info(`Status: ${err.status}: Redirecting to user-locked`);
            this.router.navigate(['/user-locked']);
          }

          this.user$ = null;
          return of(null);
        })
      )
      .subscribe();

    return this.user$;
  }

  public getUsers(): Promise<UserDto[]> {
    return lastValueFrom(this.http.get<UserDto[]>(this.baseUrl + '/web/users/'));
  }

  public async updateLanguage(languageCode: string) {
    await firstValueFrom(this.http.put(this.baseUrl + `/web/user/language/${languageCode}`, {}));

    this.user.languageCode = languageCode;
    this.translateService.use(languageCode);
    this.user$.next(this.user);
  }

  public async hasRoleAsync(role = 'app-admin') {
    const user = await this.getUserAsync();
    return user.roles.some(r => r.name === role);
  }

  public hasRole(role = 'app-admin') {
    const $hasRole = this.getUser().pipe(
      take(1),
      map(user => user.roles.some(r => r.name === role))
    );
    return $hasRole;
  }
}
